import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faPhoneVolume } from '@fortawesome/free-solid-svg-icons';
import '../styles/Navbar.css';
import LogoMark from '../assets/LogoMark.png';
import seoPages from '../data/seo-pages.json';

function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { i18n } = useTranslation();
  const [currentLang, setCurrentLang] = useState(i18n.language);
  const isDutch = currentLang?.startsWith('nl');
  const isServicePage = location.pathname === '/services' || Boolean(seoPages[location.pathname]);

  const labels = isDutch
    ? {
        home: 'Home',
        services: 'Diensten & tarieven',
        about: 'Over ons',
        faq: 'FAQ',
        contact: 'Contact',
        book: 'Reserveren',
        menu: 'Menu openen',
        close: 'Menu sluiten'
      }
    : {
        home: 'Accueil',
        services: 'Services & tarifs',
        about: 'À propos',
        faq: 'FAQ',
        contact: 'Contact',
        book: 'Réserver',
        menu: 'Ouvrir le menu',
        close: 'Fermer le menu'
      };

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  useEffect(() => {
    document.body.classList.toggle('menu-open', isMenuOpen);
    return () => document.body.classList.remove('menu-open');
  }, [isMenuOpen]);

  useEffect(() => {
    const updateNavbar = () => setIsScrolled(window.scrollY > 28);
    updateNavbar();
    window.addEventListener('scroll', updateNavbar, { passive: true });
    return () => window.removeEventListener('scroll', updateNavbar);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const toggleLanguage = () => {
    const newLang = currentLang === 'fr' ? 'nl' : 'fr';
    i18n.changeLanguage(newLang);
    setCurrentLang(newLang);
  };

  return (
    <header className={`site-header ${isScrolled ? 'is-scrolled' : ''} ${isMenuOpen ? 'menu-is-open' : ''}`}>
      <nav className="navbar" aria-label="Navigation principale">
        <Link to="/" className="brand" onClick={closeMenu} aria-label="Main Tendue, accueil">
          <span className="brand-mark">
            <img src={LogoMark} alt="" className="brand-logo" />
          </span>
          <span className="brand-name">Main <strong>Tendue</strong></span>
        </Link>

        <button
          className={`menu-toggle ${isMenuOpen ? 'active' : ''}`}
          onClick={toggleMenu}
          aria-label={isMenuOpen ? labels.close : labels.menu}
          aria-expanded={isMenuOpen}
          aria-controls="main-navigation"
        >
          <span></span><span></span><span></span>
        </button>

        <div id="main-navigation" className={`nav-panel ${isMenuOpen ? 'active' : ''}`}>
          <ul className="nav-links">
            <li><Link to="/" className={location.pathname === '/' ? 'active' : ''} onClick={closeMenu}><span>{labels.home}</span></Link></li>
            <li><Link to="/services" className={isServicePage ? 'active' : ''} onClick={closeMenu}><span>{labels.services}</span><FontAwesomeIcon className="nav-link-arrow" icon={faArrowRight} aria-hidden="true" /></Link></li>
            <li><Link to="/about" className={location.pathname === '/about' ? 'active' : ''} onClick={closeMenu}><span>{labels.about}</span><FontAwesomeIcon className="nav-link-arrow" icon={faArrowRight} aria-hidden="true" /></Link></li>
            <li><Link to="/faq" className={location.pathname === '/faq' ? 'active' : ''} onClick={closeMenu}><span>{labels.faq}</span><FontAwesomeIcon className="nav-link-arrow" icon={faArrowRight} aria-hidden="true" /></Link></li>
            <li><Link to="/contact" className={location.pathname === '/contact' ? 'active' : ''} onClick={closeMenu}><span>{labels.contact}</span><FontAwesomeIcon className="nav-link-arrow" icon={faArrowRight} aria-hidden="true" /></Link></li>
          </ul>

          <div className="nav-actions">
            <a className="nav-phone" href="tel:+32491333777" aria-label="Appeler le +32 491 33 37 77">
              <span className="nav-phone-icon" aria-hidden="true">
                <FontAwesomeIcon icon={faPhoneVolume} />
              </span>
              <span>+32 491 33 37 77</span>
            </a>
            <button className="language-button" onClick={toggleLanguage} aria-label="Changer de langue">
              {isDutch ? 'FR' : 'NL'}
            </button>
            <Link to="/reservation" className="nav-book" onClick={closeMenu}>
              {labels.book}<FontAwesomeIcon icon={faArrowRight} />
            </Link>
          </div>
        </div>
      </nav>
    </header>
  );
}

export default Navbar;
